import React, { FC, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  MonitoringFormCriteriaBlockCreateType,
  MonitoringFormCriteriaBlockObjectType,
} from '@app/interfaces/qm-module/qm-manage/qm-monitoring-forms.type';
import {
  formBaseButtonBlockStyles,
  formBaseContainerStyles,
  formFieldBaseStylesModal650,
  formFieldNameBaseStyles,
} from '@app/styles/base-styles';
import { Button, CircleButton, ColorInput, Input, Toggle } from '@ui';
import {
  useCreateMFBlockMutation,
  useEditMFBlockMutation,
} from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-forms.api';
import { useParams } from 'react-router-dom';
import useTranslation from '@app/hooks/use-translation';
type FormType = Omit<MonitoringFormCriteriaBlockCreateType, 'mform_id'>;

type MFormBlockFormPropsType = {
  mFBlockEditableData: MonitoringFormCriteriaBlockObjectType | undefined;
  onClose?: () => void;
};

const MFormBlockForm: FC<MFormBlockFormPropsType> = (props) => {
  const { onClose, mFBlockEditableData } = props;
  const { t } = useTranslation(
    'pages.qmManage.monitoring_forms.createMonitoringForm.criteria_table.create_block_modal',
  );
  const { id } = useParams();
  const [conditionalFormattingStatus, setConditionalFormattingStatus] = useState(
    !!mFBlockEditableData?.conditional_formatting.length || false,
  );
  // api
  const [createMFBlock] = useCreateMFBlockMutation();
  const [editMFBlock] = useEditMFBlockMutation();
  // api
  const { register, handleSubmit, control, watch, setValue } = useForm<FormType>({
    defaultValues: mFBlockEditableData ? { ...mFBlockEditableData } : {},
  });
  const {
    fields,
    append: appendMainConditionalFormatting,
    remove: removeMainConditionalFormatting,
  } = useFieldArray({
    name: 'conditional_formatting',
    control,
  });
  function onSubmit(values: FormType) {
    if (mFBlockEditableData) {
      editMFBlock({ body: { ...values }, params: { id: mFBlockEditableData.id } })
        .unwrap()
        .then(() => onClose?.());
      return;
    }
    id &&
      createMFBlock({ ...values, mform_id: id })
        .unwrap()
        .then(() => onClose?.());
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={formBaseContainerStyles}>
      <div className={formFieldBaseStylesModal650}>
        <span className={formFieldNameBaseStyles}>{t('name')}</span>
        <Input {...register('name')} />
      </div>
      <div className={formFieldBaseStylesModal650}>
        <span className={formFieldNameBaseStyles}>{t('description')}</span>
        <Input {...register('description')} />
      </div>
      <>
        <div className={formFieldBaseStylesModal650}>
          <span className={formFieldNameBaseStyles}>{t('conditional_formatting')}</span>
          <Toggle
            checked={conditionalFormattingStatus}
            onChecked={(val) => setConditionalFormattingStatus(val)}
          />
        </div>
        {conditionalFormattingStatus && (
          <div className="flex flex-col gap-[10px] self-end">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="grid grid-cols-[100px_100px_200px_auto_auto] items-center gap-x-[10px]"
              >
                <Input type="number" {...register(`conditional_formatting.${index}.value.0`)} />
                <Input type="number" {...register(`conditional_formatting.${index}.value.1`)} />
                <Input
                  placeholder={t('description')}
                  {...register(`conditional_formatting.${index}.desc`)}
                />
                <ColorInput
                  color={watch(`conditional_formatting.${index}.color`)}
                  changeColor={(color) => setValue(`conditional_formatting.${index}.color`, color)}
                />
                <CircleButton
                  icon={'TrashIcon'}
                  size={16}
                  className="text-basic_red"
                  onClick={() => removeMainConditionalFormatting(index)}
                />
              </div>
            ))}
            <div className="self-end flex">
              <CircleButton
                icon={'PlusIcon'}
                size={16}
                className="text-action"
                onClick={() =>
                  appendMainConditionalFormatting({ value: [0, 0], color: '#000000', desc: '' })
                }
              />
            </div>
          </div>
        )}
      </>
      {/*BUTTON BLOCK*/}
      <div className={formBaseButtonBlockStyles}>
        <Button type="submit" label={t('approve_button')} />
        <Button fill="linked" onClick={onClose} label={t('cancel_button')} />
      </div>
      {/*BUTTON BLOCK*/}
    </form>
  );
};

export default MFormBlockForm;
